import type { UserCreate } from '@erp/shared';
import {
  AlertTriangle, KeyRound, MoreHorizontal, Pencil, ShieldPlus, Trash2,
  UserCheck, UserMinus, UserPlus, Wrench,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/shadcn/index.js';
import {
  Badge,
  Button,
  Card,
  confirmDialog,
  CopyableCode,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Skeleton,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatDate } from '../../lib/intl.js';
import {
  createRole,
  createUser,
  deleteUser,
  listRoles,
  listUsers,
  repairUserLogin,
  setUserStatus,
  type RoleOption,
  type UserCreated,
  type UserListItem,
} from '../../lib/users.js';

import { EditUserDialog, type Handover, ResetPasswordDialog } from './UserDialogs.js';
import { useUserError } from './userErrors.js';

export interface UsersPageProps {
  /**
   * Directory mode: list only users whose roles grant this permission
   * (e.g. `delivery.deliver` → drivers). The server does the filtering, so
   * this stays a plain read of `/users` gated by `users.read`.
   */
  onlyPermission?: string;
  /** Heading override, resolved in the `users` namespace (e.g. `drivers.title`). */
  titleKey?: string;
  /** Sub-heading shown under the title in directory mode. */
  descriptionKey?: string;
  /** Empty-state copy for directory mode. */
  emptyKey?: string;
  /**
   * Enables the "add" action in directory mode, with copy resolved under this
   * key (e.g. `drivers.add` → `drivers.add.title`, `.subtitle`, …). The role
   * picker is then restricted to roles granting `onlyPermission`, so whoever
   * is created definitely shows up in THIS list.
   */
  addKey?: string;
  /**
   * Offered when the tenant has no role granting `onlyPermission`: create this
   * role (find-or-create) with that permission so the flow never dead-ends.
   */
  fallbackRole?: { code: string; name: string };
  /** Row click target base — `${rowLinkBase}/${user.id}` (e.g. a profile page). */
  rowLinkBase?: string;
}

export function UsersPage({
  onlyPermission,
  titleKey,
  descriptionKey,
  emptyKey,
  addKey,
  fallbackRole,
  rowLinkBase,
}: UsersPageProps = {}): JSX.Element {
  const { t, i18n } = useTranslation('users');
  const navigate = useNavigate();
  const userError = useUserError();
  // Plain /settings/users keeps the generic invite form. A DIRECTORY only gets
  // an add action when the route opts in with `addKey` — otherwise the person
  // created could land outside the filter and appear to vanish.
  const mayInvite = usePermission('users.invite');
  const canInvite = mayInvite && (!onlyPermission || Boolean(addKey));
  const canWrite = usePermission('users.write');
  // `users.delete` is owner-only in practice (it isn't in ADMIN_PERMISSION_CODES),
  // so Remove is simply invisible to admins. Deactivate covers the real cases.
  const canDelete = usePermission('users.delete');
  const me = useAuthStore((s) => s.user);
  const [users, setUsers] = useState<UserListItem[] | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  /** A credential to hand over — from a create OR a password reset. Shown once. */
  const [handover, setHandover] = useState<(Handover & { kind: 'created' | 'reset' }) | null>(null);
  const [editing, setEditing] = useState<UserListItem | null>(null);
  const [resetting, setResetting] = useState<UserListItem | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    setUsers(await listUsers(onlyPermission));
  }, [onlyPermission]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const ns = (suffix: string): string => (addKey ? `${addKey}.${suffix}` : `invite.${suffix}`);

  /** Reversible: the tenant row and the platform login record both stay put. */
  const onToggleStatus = async (u: UserListItem): Promise<void> => {
    const disabling = u.status === 'active';
    const ok = await confirmDialog({
      title: t(disabling ? 'actions.confirmDeactivateTitle' : 'actions.confirmReactivateTitle', { name: u.fullName }),
      message: t(disabling ? 'actions.confirmDeactivate' : 'actions.confirmReactivate'),
      confirmLabel: t(disabling ? 'actions.deactivate' : 'actions.reactivate'),
      tone: disabling ? 'danger' : 'default',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await setUserStatus(u.id, disabling ? 'disabled' : 'active');
      toast.success(t(disabling ? 'actions.deactivated' : 'actions.reactivated', { name: u.fullName }));
      await reload();
    } catch (err) {
      toast.error(userError(err));
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (u: UserListItem): Promise<void> => {
    const ok = await confirmDialog({
      title: t('actions.confirmRemoveTitle', { name: u.fullName }),
      message: t('actions.confirmRemove'),
      confirmLabel: t('actions.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteUser(u.id);
      toast.success(t('actions.removed', { name: u.fullName }));
      await reload();
    } catch (err) {
      toast.error(userError(err));
    } finally {
      setBusy(false);
    }
  };

  /** Re-point the platform login record at this user's current email. */
  const onFixSignIn = async (u: UserListItem): Promise<void> => {
    setBusy(true);
    try {
      await repairUserLogin(u.id);
      toast.success(t('signIn.fixed', { name: u.fullName }));
      await reload();
    } catch (err) {
      toast.error(userError(err));
    } finally {
      setBusy(false);
    }
  };

  const brokenCount = (users ?? []).filter((u) => u.signInIssue).length;

  const columns: DataTableColumn<UserListItem>[] = [
    {
      id: 'fullName',
      header: t('list.columns.fullName'),
      sortValue: (u) => u.fullName,
      searchValue: (u) => u.fullName,
      cell: (u) => (
        <>
          {u.fullName}
          {me?.id === u.id && <Badge tone="brand" className="ms-2">{t('list.you')}</Badge>}
        </>
      ),
    },
    {
      id: 'email',
      header: t('list.columns.email'),
      sortValue: (u) => u.email,
      searchValue: (u) => u.email,
      cell: (u) => <span dir="ltr">{u.email}</span>,
    },
    {
      id: 'phone',
      header: t('list.columns.phone'),
      sortValue: (u) => u.phone ?? '',
      searchValue: (u) => u.phone ?? '',
      cell: (u) => (u.phone ? <span dir="ltr">{u.phone}</span> : '—'),
    },
    {
      id: 'roles',
      header: t('list.columns.roles'),
      sortValue: (u) => (u.roles ?? []).join(', '),
      searchValue: (u) => (u.roles ?? []).join(', '),
      cell: (u) =>
        (u.roles ?? []).length === 0 ? (
          '—'
        ) : (
          <span className="flex flex-wrap gap-1">
            {u.roles!.map((r) => (
              <Badge key={r} tone="neutral">{r}</Badge>
            ))}
          </span>
        ),
    },
    {
      id: 'status',
      header: t('list.columns.status'),
      sortValue: (u) => t(`status.${u.status}`),
      searchValue: (u) => t(`status.${u.status}`),
      cell: (u) => (
        <span className="flex flex-wrap items-center gap-1">
          <Badge tone={u.status === 'active' ? 'success' : u.status === 'invited' ? 'info' : 'neutral'}>
            {t(`status.${u.status}`)}
          </Badge>
          {/* The state the owner cannot otherwise see: the tenant row looks fine
              but nothing on the platform can route a login to it. */}
          {u.signInIssue && (
            <Badge tone="warning" title={t(`signIn.reason.${u.signInIssue}`)}>
              <AlertTriangle className="me-1 h-3 w-3" aria-hidden />
              {t('signIn.badge')}
            </Badge>
          )}
        </span>
      ),
    },
    {
      id: 'lastLoginAt',
      header: t('list.columns.lastLoginAt'),
      sortValue: (u) => u.lastLoginAt ?? '',
      cell: (u) => (u.lastLoginAt ? formatDate(u.lastLoginAt, { locale: i18n.language }) : '—'),
    },
    {
      id: 'createdAt',
      header: t('list.columns.createdAt'),
      sortValue: (u) => u.createdAt,
      cell: (u) => formatDate(u.createdAt, { locale: i18n.language }),
    },
    // Additive: nothing here renders unless the viewer can actually act, so the
    // drivers directory stays a directory for anyone without users.write.
    ...(canWrite || canDelete
      ? [{
        id: 'actions',
        header: t('list.columns.actions'),
        align: 'end' as const,
        headClassName: 'w-16',
        cell: (u: UserListItem) => (
          <RowActions
            user={u}
            isSelf={me?.id === u.id}
            canWrite={canWrite}
            canDelete={canDelete}
            busy={busy}
            onEdit={() => setEditing(u)}
            onSetPassword={() => setResetting(u)}
            onToggleStatus={() => void onToggleStatus(u)}
            onRemove={() => void onRemove(u)}
            onFixSignIn={() => void onFixSignIn(u)}
          />
        ),
      }]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{t(titleKey ?? 'title')}</h1>
          {descriptionKey && <p className="mt-1 text-sm text-fg-muted">{t(descriptionKey)}</p>}
        </div>
        {canInvite && !showInvite && (
          <Button onClick={() => { setHandover(null); setShowInvite(true); }}>{t(ns('title'))}</Button>
        )}
      </div>

      {/* One-time credential hand-off. Rendered above the list so it can't be
          missed — the password is not recoverable after this. */}
      {handover && (
        <NewCredentials handover={handover} onDismiss={() => setHandover(null)} />
      )}

      {/* Visible without scanning a column: someone here is locked out. */}
      {brokenCount > 0 && (
        <Card className="border-warning/40 bg-warning/5">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-warning/15 text-warning">
              <AlertTriangle className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold">{t('signIn.banner.title')}</h2>
              <p className="mt-1 text-sm text-fg-muted">
                {t(canWrite ? 'signIn.banner.description' : 'signIn.banner.descriptionReadOnly')}
              </p>
            </div>
          </div>
        </Card>
      )}

      {showInvite && (
        <InviteUserForm
          copyKey={addKey}
          rolePermission={onlyPermission}
          fallbackRole={fallbackRole}
          onCancel={() => setShowInvite(false)}
          onInvited={async (u) => {
            setShowInvite(false);
            if (u.tempPassword) {
              setHandover({ kind: 'created', fullName: u.fullName, email: u.email, password: u.tempPassword });
            }
            await reload();
          }}
        />
      )}

      {!users ? (
        <Card>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="mt-4 h-32 w-full" />
        </Card>
      ) : users.length === 0 ? (
        <EmptyState
          icon={<UserPlus className="h-8 w-8" aria-hidden />}
          title={t(emptyKey ? `${emptyKey}.title` : 'list.empty')}
          description={t(emptyKey ? `${emptyKey}.description` : 'list.emptyDescription')}
          action={
            canInvite ? (
              <Button size="sm" onClick={() => { setHandover(null); setShowInvite(true); }}>
                {t(ns('title'))}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={users}
            rowKey={(u) => u.id}
            searchPlaceholder={t('list.columns.email')}
            onRowClick={rowLinkBase ? (u) => navigate(`${rowLinkBase}/${u.id}`) : undefined}
          />
        </Card>
      )}

      {editing && (
        <EditUserDialog
          user={editing}
          isSelf={me?.id === editing.id}
          onClose={() => setEditing(null)}
          onSaved={reload}
        />
      )}
      {resetting && (
        <ResetPasswordDialog
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={(h) => setHandover({ ...h, kind: 'reset' })}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The one-per-row actions menu — a single "⋯" trigger so a directory view
 * (Persons → Drivers) gains full control without gaining four buttons per row.
 *
 * Self-awareness is mirrored from the server, not invented here: the endpoints
 * refuse a self password reset, self-deactivate and self-delete, so offering
 * them would only produce a 422. Editing your own name stays available.
 */
function RowActions({
  user, isSelf, canWrite, canDelete, busy, onEdit, onSetPassword, onToggleStatus, onRemove, onFixSignIn,
}: {
  user: UserListItem;
  isSelf: boolean;
  canWrite: boolean;
  canDelete: boolean;
  busy: boolean;
  onEdit: () => void;
  onSetPassword: () => void;
  onToggleStatus: () => void;
  onRemove: () => void;
  onFixSignIn: () => void;
}): JSX.Element {
  const { t } = useTranslation('users');
  /**
   * Every item here opens a modal (a dialog or a confirm). Radix returns focus
   * to the trigger as the menu closes, so a modal mounted in the SAME tick
   * fights it for focus — defer by a frame and the menu is fully gone first.
   */
  const defer = (fn: () => void) => (): void => { window.setTimeout(fn, 0); };
  return (
    // stopPropagation so acting on a row doesn't also fire the row-click
    // navigation the drivers directory installs.
    <div className="flex justify-end" onClick={(e) => e.stopPropagation()} role="presentation">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="px-2" aria-label={t('actions.menu')} disabled={busy}>
            <MoreHorizontal className="h-4 w-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canWrite && user.signInIssue && (
            <>
              <DropdownMenuItem onSelect={defer(onFixSignIn)} className="text-warning">
                <Wrench className="h-4 w-4" aria-hidden />
                {t('signIn.fix')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {canWrite && (
            <DropdownMenuItem onSelect={defer(onEdit)}>
              <Pencil className="h-4 w-4" aria-hidden />
              {t('actions.edit')}
            </DropdownMenuItem>
          )}
          {canWrite && !isSelf && (
            <DropdownMenuItem onSelect={defer(onSetPassword)}>
              <KeyRound className="h-4 w-4" aria-hidden />
              {t('actions.setPassword')}
            </DropdownMenuItem>
          )}
          {canWrite && !isSelf && (
            <DropdownMenuItem onSelect={defer(onToggleStatus)}>
              {user.status === 'active'
                ? <UserMinus className="h-4 w-4" aria-hidden />
                : <UserCheck className="h-4 w-4" aria-hidden />}
              {t(user.status === 'active' ? 'actions.deactivate' : 'actions.reactivate')}
            </DropdownMenuItem>
          )}
          {canDelete && !isSelf && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={defer(onRemove)} className="text-danger">
                <Trash2 className="h-4 w-4" aria-hidden />
                {t('actions.remove')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** A password shown ONCE — from a create or a reset. Never recoverable after. */
function NewCredentials({
  handover, onDismiss,
}: { handover: Handover & { kind: 'created' | 'reset' }; onDismiss: () => void }): JSX.Element {
  const { t } = useTranslation('users');
  const { t: tc } = useTranslation();
  const reset = handover.kind === 'reset';
  // This card is the ONLY time the password is ever readable. It renders above
  // the table, so from a scrolled-down row it would otherwise appear off-screen
  // and the credential would be lost for good.
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, []);
  return (
    <Card className="border-success/40 bg-success/5">
      <div ref={ref} className="flex items-start gap-3">
        <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-success/15 text-success">
          <KeyRound className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold">
            {t(reset ? 'credentials.resetTitle' : 'credentials.title', { name: handover.fullName })}
          </h2>
          <p className="mt-1 text-sm text-fg-muted">
            {t(reset ? 'credentials.resetDescription' : 'credentials.description')}
          </p>
          <div className="mt-3 flex max-w-lg flex-col gap-2">
            <CopyableCode value={handover.email} label={t('invite.email')} truncate />
            <CopyableCode
              value={handover.password}
              label={t(reset ? 'credentials.resetPassword' : 'credentials.password')}
              truncate
            />
          </div>
          <p className="mt-2 text-xs text-warning">{t('credentials.warning')}</p>
        </div>
        <Button size="sm" variant="secondary" onClick={onDismiss}>
          {tc('actions.dismiss', { defaultValue: 'Dismiss' })}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface InviteFormProps {
  /** Copy namespace override (directory mode), e.g. `drivers.add`. */
  copyKey?: string;
  /** Restrict the role picker to roles granting this permission. */
  rolePermission?: string;
  fallbackRole?: { code: string; name: string };
  onCancel: () => void;
  onInvited: (user: UserCreated) => void | Promise<void>;
}

function InviteUserForm({
  copyKey,
  rolePermission,
  fallbackRole,
  onCancel,
  onInvited,
}: InviteFormProps): JSX.Element {
  const { t } = useTranslation('users');
  const { t: tc } = useTranslation();
  const userError = useUserError();
  const [roles, setRoles] = useState<RoleOption[] | null>(null);
  const [form, setForm] = useState<UserCreate>({
    email: '',
    fullName: '',
    phone: '',
    locale: 'en',
    roleIds: [],
  });
  /** Off = server generates a temporary password and returns it once. */
  const [setOwnPassword, setSetOwnPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [creatingRole, setCreatingRole] = useState(false);

  const key = useCallback(
    (suffix: string): string => (copyKey ? `${copyKey}.${suffix}` : `invite.${suffix}`),
    [copyKey],
  );

  const loadRoles = useCallback(async (): Promise<RoleOption[]> => {
    const items = await listRoles(rolePermission);
    setRoles(items);
    return items;
  }, [rolePermission]);

  useEffect(() => {
    void loadRoles();
  }, [loadRoles]);

  // A directory with exactly one qualifying role shouldn't make you tick a box.
  useEffect(() => {
    if (rolePermission && roles?.length === 1) setForm((f) => (f.roleIds.length ? f : { ...f, roleIds: [roles[0]!.id] }));
  }, [roles, rolePermission]);

  const noQualifyingRole = useMemo(
    () => Boolean(rolePermission) && roles !== null && roles.length === 0,
    [rolePermission, roles],
  );
  /**
   * Whether to offer minting the dedicated role. The permission that defines a
   * directory is usually ALSO held by owner/admin (the delivery migration grants
   * `delivery.deliver` to both), so the picker is rarely empty — but "Owner" is
   * a terrible thing to tick when you mean "driver". Offer the purpose-built
   * role whenever it doesn't exist yet, not only when the list is empty.
   */
  const canMintRole = usePermission('roles.write');
  const offerFallbackRole =
    Boolean(fallbackRole) && canMintRole && roles !== null
    && !roles.some((r) => r.code === fallbackRole!.code);

  const toggleRole = (id: string): void => {
    setForm((f) => ({
      ...f,
      roleIds: f.roleIds.includes(id)
        ? f.roleIds.filter((x) => x !== id)
        : [...f.roleIds, id],
    }));
  };

  /** No role grants the permission — mint one so the flow can continue. */
  const onCreateFallbackRole = async (): Promise<void> => {
    if (!fallbackRole || !rolePermission) return;
    setCreatingRole(true);
    setError(null);
    try {
      const role = await createRole({ ...fallbackRole, permissionCodes: [rolePermission] });
      const items = await loadRoles();
      // Select it even if the refreshed list hasn't propagated yet.
      setForm((f) => ({ ...f, roleIds: [items.find((r) => r.id === role.id)?.id ?? role.id] }));
    } catch (err) {
      setError(userError(err));
    } finally {
      setCreatingRole(false);
    }
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (form.roleIds.length === 0) {
      setError(t('invite.errors.roleRequired'));
      return;
    }
    if (setOwnPassword && password.length < 8) {
      setError(t('invite.errors.passwordTooShort'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const user = await createUser({
        ...form,
        phone: form.phone?.trim() || undefined,
        password: setOwnPassword ? password : undefined,
      });
      await onInvited(user);
    } catch (err) {
      setError(userError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2 className="mb-1 text-lg font-semibold">{t(key('title'))}</h2>
      <p className="mb-4 text-sm text-fg-muted">{t(key('subtitle'))}</p>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="inv-name" label={t('invite.fullName')} required>
          <Input
            id="inv-name"
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            required
            autoFocus
          />
        </FormField>
        <FormField id="inv-email" label={t('invite.email')} required>
          <Input
            id="inv-email"
            type="email"
            dir="ltr"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </FormField>
        <FormField id="inv-phone" label={t('invite.phone')}>
          <Input
            id="inv-phone"
            type="tel"
            dir="ltr"
            value={form.phone ?? ''}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </FormField>
        <FormField id="inv-locale" label={t('invite.locale')}>
          <select
            id="inv-locale"
            value={form.locale}
            onChange={(e) => setForm({ ...form, locale: e.target.value as UserCreate['locale'] })}
            className="rounded border border-border bg-bg px-3 py-2 text-sm"
          >
            <option value="en">English</option>
            <option value="ar">العربية</option>
          </select>
        </FormField>

        {/* Credentials. Either the caller sets one now, or the server generates
            a temporary one and returns it once for hand-over. */}
        <fieldset className="md:col-span-2 rounded border border-border p-3">
          <legend className="px-2 text-sm font-medium">{t('invite.password')}</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={setOwnPassword}
              onChange={(e) => setSetOwnPassword(e.target.checked)}
            />
            <span>{t('invite.setPassword')}</span>
          </label>
          {setOwnPassword ? (
            <div className="mt-2 max-w-sm">
              <FormField id="inv-password" label={t('invite.password')} required>
                <Input
                  id="inv-password"
                  type="password"
                  dir="ltr"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                />
              </FormField>
            </div>
          ) : (
            <p className="mt-2 text-xs text-fg-muted">{t('invite.generateHint')}</p>
          )}
        </fieldset>

        <fieldset className="md:col-span-2 rounded border border-border p-3">
          <legend className="px-2 text-sm font-medium">{t('invite.roles')}</legend>
          <p className="mb-2 text-xs text-fg-muted">
            {t(rolePermission ? key('rolesHint') : 'invite.rolesHint')}
          </p>
          {roles === null ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <div className="flex flex-col items-start gap-2">
              {noQualifyingRole ? (
                // Dead-end guard: nothing here grants the permission at all.
                <p className="text-sm text-fg-muted">
                  {t(rolePermission ? key('noQualifyingRole') : 'invite.noRoles')}
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {roles.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.roleIds.includes(r.id)}
                        onChange={() => toggleRole(r.id)}
                      />
                      <span>{r.name}</span>
                      <span className="font-mono text-xs text-fg-muted">{r.code}</span>
                    </label>
                  ))}
                </div>
              )}
              {offerFallbackRole && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  loading={creatingRole}
                  onClick={() => void onCreateFallbackRole()}
                >
                  <ShieldPlus className="h-4 w-4" aria-hidden />
                  {t(key('createRole'), { role: fallbackRole!.name })}
                </Button>
              )}
            </div>
          )}
        </fieldset>

        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving} disabled={noQualifyingRole}>
            {t(key('submit'))}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {tc('actions.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
